import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import "../index.css";


const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: custom => ({
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
      delay: custom * 0.1
    }
  }),
  hover: {
    y: -10,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 15
    }
  }
};
// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const headingVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const letterVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 16,
    },
  },
};

const tabVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5 }
  },
  exit: { 
    opacity: 0,
    y: -20,
    transition: { duration: 0.3 }
  },
};

const formItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: custom => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: custom * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const hoverButtonVariants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.05,
    boxShadow: "0 6px 20px rgba(253, 172, 0, 0.4)",
    transition: { 
      duration: 0.3, 
      ease: "easeInOut",
      yoyo: Infinity
    }
  },
  tap: { scale: 0.98 }
};

export default function ContactArea() {
  const [activeTab, setActiveTab] = useState("primary");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [isSending, setIsSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load Calendly assets dynamically
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
      if (document.head.contains(script)) document.head.removeChild(script);
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const sendEmail = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSending(true);

    const serviceID = "service_psc7inq";
    const templateID = "template_q8ljehd";
    const publicKey = "j2fVhhwgRzBF9s-wo";

    emailjs
      .send(
        serviceID,
        templateID,
        {
          name: formData.fullName,
          email: formData.email,
          mobile: formData.mobile,
          message: formData.message,
        },
        publicKey
      )
      .then(
        (result) => {
          console.log("Email sent successfully:", result);
          setIsSubmitted(true);
          setFormData({ fullName: "", email: "", mobile: "", message: "" });
        },
        (error) => {
          console.error("Email sending failed:", error);
          alert(
            "Failed to send message. Please check the console for details."
          );
        }
      )
      .finally(() => {
        setIsSending(false);
      });
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      fullName: "",
      email: "",
      mobile: "",
      message: "",
    });
    setErrors({});
  };

  const openCalendlyPopup = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/thepodsutra/30min",
      });
    } else {
      console.error("Calendly widget not loaded");
    }
  };

  // Contact information for Primary tab
  const contactInfo = [
    { 
      icon: "📞", 
      title: "Call Us",
      content: "+91 76220 04433",
      action: "tel:+917622004433"
    },
    { 
      icon: "📧", 
      title: "Email Us",
      content: "thepodsutra@gmail.com",
      action: () => window.open("mailto:thepodsutra@gmail.com", "_self")
    },
    { 
      icon: "📍",   
      title: "Visit Us",
      content: "109, Triumph Plaza, Canal Road, Gaurav Path Road, Surat, Gujarat-595009",
      action: " https://www.google.co.in/maps/place/The+PodSutra+Studios/@21.2024359,72.7655272,17z/data=!3m1!4b1!4m6!3m5!1s0x3be04d3faba83d59:0x6eaf9ab2fe122a98!8m2!3d21.2024309!4d72.7681021!16s%2Fg%2F11m5nlzmj3?entry=ttu&g_ep=EgoyMDI1MDQxNC4xIKXMDSoASAFQAw%3D%3D "
    }
  ];

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      style={{
        padding: "3.5rem 1.5rem",
        width: "100%",
        
      }}
      id="Contact"
    >
      <div style={{ maxWidth: "60rem", margin: "0 auto" }}>
        <motion.h2
          variants={headingVariants}
          style={{
            fontSize: "clamp(2rem, 5vw, 2.75rem)",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "2px",
            marginBottom: "3rem",
            textAlign: "center",
          }}
        >
          {Array.from("Contact Us").map((char, index) => (
            <motion.span
              key={index}
              style={{
                display: "inline-block",
                color: "#fdac00",
                padding: "0 2px",
                cursor: "default",
                textShadow: "0 2px 10px rgba(253, 172, 0, 0.2)",
              }}
              variants={letterVariants}
              whileHover={{ 
                y: -8, 
                color: "#e59a00",
                transition: { type: "spring", stiffness: 300, damping: 10 }
              }}
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            textAlign: "center",
            gap: "0.75rem",
            marginBottom: "2rem",
            flexWrap: "wrap",
          }}
        >
          {["primary", "ContactUs", "map"].map((tab, index) => (
            <motion.button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                if (tab === "ContactUs") setIsSubmitted(false);
              }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 4px 15px rgba(253, 172, 0, 0.3)" 
              }}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: "0.75rem 2rem",
                borderRadius: "9999px",
                fontSize: "0.95rem",
                fontWeight: 600,
                border: "none",
                outline: "none",
                cursor: "pointer",
                transition: "all 0.3s ease",
                backgroundColor: activeTab === tab ? "#fdac00" : "#f0f0f0",
                color: activeTab === tab ? "#000" : "#4b5563",
                boxShadow: activeTab === tab 
                  ? "0 4px 15px rgba(253, 172, 0, 0.3)" 
                  : "0 2px 10px rgba(0, 0, 0, 0.05)",
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </motion.button>
          ))}
        </motion.div>

        <motion.div
          layout
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            backgroundColor: "#fffaf0", // Light cream color
            padding: "0",
            borderRadius: "1rem",
            boxShadow: "0 10px 30px rgba(253, 172, 0, 0.1)",
            overflow: "hidden",
            minHeight: "26rem",
            border: "1px solid rgba(253, 172, 0, 0.1)",
          }}
        >
          <AnimatePresence mode="wait">
            {activeTab === "ContactUs" ? (
              isSubmitted ? (
                <motion.div
                  key="success"
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "3rem 2rem",
                    minHeight: "26rem",
                    background: "linear-gradient(135deg, #fffaf0 0%, #fff8e1 100%)",
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ 
                      scale: [0, 1.2, 1],
                      rotate: [0, 10, 0]
                    }}
                    transition={{ 
                      duration: 0.6,
                      ease: "easeOut",
                      times: [0, 0.7, 1],
                      delay: 0.2 
                    }}
                    style={{
                      backgroundColor: "#f0f9eb",
                      borderRadius: "50%",
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "1.5rem",
                      boxShadow: "0 6px 20px rgba(82, 196, 26, 0.2)",
                      border: "1px solid #e6f7d9",
                    }}
                  >
                    <motion.svg 
                      width="40" 
                      height="40" 
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#52c41a"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </motion.svg>
                  </motion.div>
                  
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    style={{
                      fontSize: "2rem",
                      fontWeight: "bold",
                      color: "#fdac00",
                      marginBottom: "1rem",
                      textShadow: "0 2px 10px rgba(253, 172, 0, 0.1)",
                    }}
                  >
                    Thank You!
                  </motion.h3>
                  
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    style={{
                      fontSize: "1.2rem",
                      color: "#4b5563",
                      marginBottom: "2rem",
                      maxWidth: "32rem",
                      lineHeight: 1.6,
                    }}
                  >
                    Your message has been sent successfully. We'll get back to you
                    soon!
                  </motion.p>
                  
                  <motion.button
                    onClick={resetForm}
                    initial="initial"
                    whileHover="hover"
                    whileTap="tap" 
                    variants={hoverButtonVariants}
                    style={{
                      padding: "0.75rem 2rem",
                      border: "none",
                      borderRadius: "9999px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      backgroundColor: "#fdac00",
                      color: "black",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(253, 172, 0, 0.3)",
                    }}
                  >
                    Send Another Message
                  </motion.button>
                </motion.div>
              ) : (
                <motion.form
                  key="contactForm"
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onSubmit={sendEmail}
                  style={{ 
                    display: "grid", 
                    gap: "1.25rem",
                    padding: "2rem", 
                    background: "linear-gradient(135deg, #fffaf0 0%, #fff8e1 100%)",
                  }}
                >
                  <motion.div
                    custom={0}
                    variants={formItemVariants}
                  >
                    <div
                      style={{
                        position: "relative",
                      }}
                    >
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Full name"
                        required
                        value={formData.fullName}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          padding: "1rem 1.25rem",
                          border: errors.fullName 
                            ? "1.5px solid #ff4d4f" 
                            : "1.5px solid rgba(253, 172, 0, 0.2)",
                          borderRadius: "0.75rem",
                          outline: "none",
                          fontSize: "1rem",
                          transition: "all 0.2s",
                          backgroundColor: "white",
                          boxShadow: errors.fullName 
                            ? "0 0 0 3px rgba(255, 77, 79, 0.1)" 
                            : "0 2px 10px rgba(0, 0, 0, 0.01)",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#fdac00";
                          e.target.style.boxShadow = "0 0 0 3px rgba(253, 172, 0, 0.1)";
                        }}
                        onBlur={(e) => {
                          if (errors.fullName) {
                            e.target.style.borderColor = "#ff4d4f";
                            e.target.style.boxShadow = "0 0 0 3px rgba(255, 77, 79, 0.1)";
                          } else {
                            e.target.style.borderColor = "rgba(253, 172, 0, 0.2)";
                            e.target.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.01)";
                          }
                        }}
                      />
                      {errors.fullName && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            color: "#ff4d4f",
                            fontSize: "0.8rem",
                            marginTop: "0.5rem",
                            paddingLeft: "0.5rem"
                          }}
                        >
                          {errors.fullName}
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    custom={1}
                    variants={formItemVariants}
                  >
                    <div
                      style={{
                        position: "relative",
                      }}
                    >
                      <input
                        type="email"
                        name="email"
                        placeholder="Email address"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          padding: "1rem 1.25rem",
                          border: errors.email 
                            ? "1.5px solid #ff4d4f" 
                            : "1.5px solid rgba(253, 172, 0, 0.2)",
                          borderRadius: "0.75rem",
                          outline: "none",
                          fontSize: "1rem",
                          transition: "all 0.2s",
                          backgroundColor: "white",
                          boxShadow: errors.email 
                            ? "0 0 0 3px rgba(255, 77, 79, 0.1)" 
                            : "0 2px 10px rgba(0, 0, 0, 0.01)",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#fdac00";
                          e.target.style.boxShadow = "0 0 0 3px rgba(253, 172, 0, 0.1)";
                        }}
                        onBlur={(e) => {
                          if (errors.email) {
                            e.target.style.borderColor = "#ff4d4f";
                            e.target.style.boxShadow = "0 0 0 3px rgba(255, 77, 79, 0.1)";
                          } else {
                            e.target.style.borderColor = "rgba(253, 172, 0, 0.2)";
                            e.target.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.01)";
                          }
                        }}
                      />
                      {errors.email && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            color: "#ff4d4f",
                            fontSize: "0.8rem",
                            marginTop: "0.5rem",
                            paddingLeft: "0.5rem"
                          }}
                        >
                          {errors.email}
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    custom={2}
                    variants={formItemVariants}
                  >
                    <div
                      style={{
                        position: "relative",
                      }}
                    >
                      <input
                        type="tel"
                        name="mobile"
                        placeholder="Mobile Number"
                        required
                        value={formData.mobile}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          padding: "1rem 1.25rem",
                          border: errors.mobile 
                            ? "1.5px solid #ff4d4f" 
                            : "1.5px solid rgba(253, 172, 0, 0.2)",
                          borderRadius: "0.75rem",
                          outline: "none",
                          fontSize: "1rem",
                          transition: "all 0.2s",
                          backgroundColor: "white",
                          boxShadow: errors.mobile 
                            ? "0 0 0 3px rgba(255, 77, 79, 0.1)" 
                            : "0 2px 10px rgba(0, 0, 0, 0.01)",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#fdac00";
                          e.target.style.boxShadow = "0 0 0 3px rgba(253, 172, 0, 0.1)";
                        }}
                        onBlur={(e) => {
                          if (errors.mobile) {
                            e.target.style.borderColor = "#ff4d4f";
                            e.target.style.boxShadow = "0 0 0 3px rgba(255, 77, 79, 0.1)";
                          } else {
                            e.target.style.borderColor = "rgba(253, 172, 0, 0.2)";
                            e.target.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.01)";
                          }
                        }}
                      />
                      {errors.mobile && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            color: "#ff4d4f",
                            fontSize: "0.8rem",
                            marginTop: "0.5rem",
                            paddingLeft: "0.5rem"
                          }}
                        >
                          {errors.mobile}
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    custom={3}
                    variants={formItemVariants}
                  >
                    <div
                      style={{
                        position: "relative",
                      }}
                    >
                      <textarea
                        name="message"
                        placeholder="Message"
                        rows={4}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          padding: "1rem 1.25rem",
                          border: errors.message 
                            ? "1.5px solid #ff4d4f" 
                            : "1.5px solid rgba(253, 172, 0, 0.2)",
                          borderRadius: "0.75rem",
                          outline: "none",
                          fontSize: "1rem",
                          resize: "none",
                          transition: "all 0.2s",
                          backgroundColor: "white",
                          boxShadow: errors.message 
                            ? "0 0 0 3px rgba(255, 77, 79, 0.1)" 
                            : "0 2px 10px rgba(0, 0, 0, 0.01)",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#fdac00";
                          e.target.style.boxShadow = "0 0 0 3px rgba(253, 172, 0, 0.1)";
                        }}
                        onBlur={(e) => {
                          if (errors.message) {
                            e.target.style.borderColor = "#ff4d4f";
                            e.target.style.boxShadow = "0 0 0 3px rgba(255, 77, 79, 0.1)";
                          } else {
                            e.target.style.borderColor = "rgba(253, 172, 0, 0.2)";
                            e.target.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.01)";
                          }
                        }}
                      />
                      {errors.message && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            color: "#ff4d4f",
                            fontSize: "0.8rem",
                            marginTop: "0.5rem",
                            paddingLeft: "0.5rem"
                          }}
                        >
                          {errors.message}
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  <motion.div 
                    style={{ textAlign: "center", marginTop: "0.5rem" }}
                    custom={4}
                    variants={formItemVariants}
                  >
                    <motion.button
                      type="submit"
                      disabled={isSending}
                      initial="initial"
                      whileHover={!isSending ? "hover" : undefined}
                      whileTap={!isSending ? "tap" : undefined}
                      variants={hoverButtonVariants}
                      style={{
                        padding: "1rem 2.5rem",
                        border: "none",
                        borderRadius: "9999px",
                        fontSize: "1.125rem",
                        fontWeight: "600",
                        backgroundColor: "#fdac00",
                        color: "black",
                        cursor: isSending ? "not-allowed" : "pointer",
                        opacity: isSending ? 0.8 : 1,
                        boxShadow: "0 4px 15px rgba(253, 172, 0, 0.3)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {isSending ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24"
                            style={{
                              animation: "spin 1s linear infinite",
                            }}
                          >
                            <style>
                              {`
                                @keyframes spin {
                                  0% { transform: rotate(0deg); }
                                  100% { transform: rotate(360deg); }
                                }
                              `}
                            </style>
                            <circle 
                              cx="12" 
                              cy="12" 
                              r="10" 
                              stroke="currentColor" 
                              strokeWidth="4" 
                              fill="none" 
                              strokeDasharray="30 30" 
                              strokeDashoffset="0"
                            />
                          </svg>
                          Sending...
                        </div>
                      ) : (
                        <>
                          <motion.span
                            initial={{ y: 0 }}
                            animate={{ y: [-2, 0, -2] }}
                            transition={{ 
                              duration: 1.5, 
                              repeat: Infinity,
                              repeatType: "mirror" 
                            }}
                            style={{ 
                              display: "inline-block",
                              marginRight: "5px"
                            }}
                          >
                            ✉️
                          </motion.span>
                          Send Message
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                </motion.form>
              )
            ) : activeTab === "map" ? (
              <motion.div
                key="map"
                variants={tabVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  width: "100%",
                  height: "26rem",
                  borderRadius: "0.5rem",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(135deg, rgba(255,250,240,0.05) 0%, rgba(255,250,240,0) 100%)",
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                />
                <iframe
                  title="Google Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.7412390991703!2d72.76552717597286!3d21.2024358817894!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04d3faba83d59%3A0x6eaf9ab2fe122a98!2sThe%20PodSutra%20Studios!5e0!3m2!1sen!2sin!4v1742366960459!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </motion.div>
            ) : (
              <motion.div
                key="primary"
                variants={tabVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "2.5rem 2rem",
                  gap: "2rem",
                  background: "linear-gradient(135deg, #fffaf0 0%, #fff8e1 100%)",
                }}
              >
               <div style={{
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "1.5rem",
  width: "100%",
}}>
  {contactInfo.map((item, index) => (
    <motion.div
      key={index}
      custom={index}
      variants={cardVariants}
      whileHover="hover"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2.5rem 1.5rem",
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
        border: "1px solid #f0f0f0",
        textDecoration: "none",
        color: "inherit",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer"
      }}
      onClick={() => typeof item.action === 'function' ? item.action() : window.open(item.action)}
    >
      {/* Rest of the card content remains the same */}
      <motion.div 
        initial={{ scale: 1 }}
        whileHover={{ 
          scale: 1.1,
          rotate: [0, -5, 5, -5, 0],
          transition: { 
            duration: 0.5,
            times: [0, 0.2, 0.4, 0.6, 0.8]
          }
        }}
        style={{
          fontSize: "2.5rem",
          marginBottom: "1rem",
          backgroundColor: "rgba(253, 172, 0, 0.1)",
          borderRadius: "50%",
          width: "60px",
          height: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.icon}
      </motion.div>
      <h3 style={{
        fontSize: "1.25rem",
        fontWeight: "600",
        margin: "0 0 0.75rem 0",
        color: "#fdac00",
      }}>
        {item.title}
      </h3>
      <p style={{
        fontSize: "1rem",
        textAlign: "center",
        color: "#4b5563",
        margin: 0,
        lineHeight: 1.6,
      }}>
        {item.content}
      </p>
    </motion.div>
  ))}
</div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  style={{
                    marginTop: "1rem",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <motion.button
                    onClick={openCalendlyPopup}
                    initial="initial"
                    whileHover="hover"
                    whileTap="tap" 
                    variants={hoverButtonVariants}
                    style={{
                      padding: "1rem 2.5rem",
                      border: "none",
                      borderRadius: "9999px",
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      backgroundColor: "#fdac00",
                      color: "black",
                      cursor: "pointer",
                      boxShadow: "0 4px 15px rgba(253, 172, 0, 0.3)",
                    }}
                  >
                    <motion.span
                      initial={{ y: 0 }}
                      animate={{ y: [-2, 0, -2] }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity,
                        repeatType: "mirror" 
                      }}
                      style={{ 
                        display: "inline-block",
                        marginRight: "10px"
                      }}
                    >
                      📅
                    </motion.span>
                    Schedule a Meeting
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.section>
  );
}